import { PackageData } from "@/pages/CreatePackage";
import { TemplateStyle } from "../types";
import { companyName } from "../constants";
import { generateQrCode } from "../helpers";
import { renderCommonSection } from "../commonSections";

const renderSection = (sectionId: string, packageData: PackageData): string => {
  const common = renderCommonSection(sectionId, packageData);
  if (common !== null) return common;

  switch (sectionId) {
    case 'client': {
      const clientCompanyLogoHtml = packageData.companyLogoUrl ? `<img src="${packageData.companyLogoUrl}" alt="شعار شركة العميل" style="max-height: 40px; margin-bottom: 10px; border-radius: 4px;" />` : '';
      const companyNameHtml = packageData.companyName ? `<div class="detail-item"><strong>الشركة:</strong> ${packageData.companyName}</div>` : '';
      return `<div class="section">
                  <h3>معلومات العميل</h3>
                  ${clientCompanyLogoHtml}
                  <div class="details-grid">
                      <div class="detail-item"><strong>الاسم:</strong> ${packageData.clientName}</div>
                      <div class="detail-item"><strong>البريد:</strong> ${packageData.clientEmail}</div>
                      ${companyNameHtml}
                  </div>
              </div>`;
    }
    case 'hotels':
      if (packageData.hotels.length === 0) return '';
      return `<div class="section"><h3>الفنادق</h3><div class="details-grid">${packageData.hotels.map(hotel => `<div class="detail-item"><strong>${hotel.name}</strong>${hotel.nights} ليالي</div>`).join('')}</div></div>`;
    case 'flights':
      if (packageData.flights.length === 0) return '';
      return `<div class="section"><h3>الطيران</h3><div class="details-grid">${packageData.flights.map(flight => `<div class="detail-item"><strong>${flight.airline}</strong>من ${flight.from} إلى ${flight.to}</div>`).join('')}</div></div>`;
    case 'tours':
      if (packageData.tours.length === 0) return '';
      return `<div class="section"><h3>الجولات</h3>${packageData.tours.map(tour => `<div class="detail-item"><strong>${tour.name}</strong><br>${tour.description}</div>`).join('')}</div>`;
    case 'price':
      return `<div class="price-section"><div class="label">السعر الإجمالي</div><div class="total">$${packageData.totalPrice}</div></div>`;
    default:
      return '';
  }
};

export const getTemplate2HTML = async (packageData: PackageData, settings: TemplateStyle): Promise<string> => {
  const columnsHtml = settings.columns.map(column => {
    const sectionsHtml = column.sections.map(section => renderSection(section.id, packageData)).join('');
    return `<div class="pdf-column">${sectionsHtml}</div>`;
  }).join('');
  
  const qrCodeData = await generateQrCode(`Package: ${packageData.packageName}\nClient: ${packageData.clientName}\nPrice: $${packageData.totalPrice}`);
  
  const logoHtml = settings.media?.logo ? `<img src="${settings.media.logo}" alt="Company Logo" style="max-height: 50px;" />` : '';
  const backgroundStyle = settings.media?.background ? `background: url('${settings.media.background}') center center / cover no-repeat;` : '';

  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <title>عرض سياحي - ${packageData.packageName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap" rel="stylesheet">
      <style>
        body { font-family: 'Tajawal', sans-serif; background-color: #f4f4f4; color: ${settings.colors.text}; }
        .container { max-width: 800px; margin: 20px auto; background: white; border: 1px solid #ddd; box-shadow: 0 0 10px rgba(0,0,0,0.05); ${backgroundStyle} }
        .header { padding: 20px 30px; border-bottom: 4px solid ${settings.colors.primary}; display: flex; justify-content: space-between; align-items: center; }
        .header h1 { font-size: 24px; color: ${settings.colors.primary}; margin: 0; }
        .header .company-name { font-size: 18px; font-weight: bold; }
        .content { padding: 30px; }
        .pdf-row { display: flex; flex-direction: row; gap: 20px; }
        .pdf-column { flex: 1; min-width: 0; }
        .section { margin-bottom: 20px; }
        .section h3 { font-size: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; margin-bottom: 15px; color: ${settings.colors.primary}; }
        .details-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; }
        .detail-item { background: ${settings.colors.background}; padding: 15px; border-radius: 5px; }
        .detail-item strong { display: block; margin-bottom: 5px; color: #555; }
        .price-section { margin-top: 30px; padding: 20px; background-color: ${settings.colors.primary}; color: white; text-align: center; border-radius: 5px; }
        .price-section .label { font-size: 18px; }
        .price-section .total { font-size: 32px; font-weight: 700; }
        .footer { text-align: center; color: #777; padding: 20px; font-size: 12px; border-top: 1px solid #eee; margin-top: 20px; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <div>
            <div class="company-name">${companyName}</div>
            <h1>عرض سياحي</h1>
          </div>
          ${logoHtml}
        </div>
        <div class="content">
          <div class="section">
            <h3>${packageData.packageName || 'عرض سياحي مخصص'}</h3>
            <p>${packageData.destination} | ${packageData.duration}</p>
          </div>
          <div class="pdf-row">
            ${columnsHtml}
          </div>
          <div class="footer">
            <img src="${qrCodeData}" alt="QR Code" style="width:80px;height:80px;margin:10px auto;" />
            تاريخ الإنشاء: ${new Date().toLocaleDateString('ar-SA')}
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};
